import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  effect,
  signal,
  untracked
} from "@angular/core";
import {
  AbstractControl,
  FormGroup,
  Validators,
  ReactiveFormsModule
} from "@angular/forms";
import { FormService } from "src/app/services/form.service";
import { PriceService } from "src/app/services/price.service";
import {
  PackageResponse,
  PackageTagEnum,
  PropertyEnum,
  PackageTypeEnum,
  CalendarDayTimeSlotResponse,
  CalendarTimeSlotResponse,
  BusinessProposalResponse
} from "src/domain/client";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import { CarBrandPipe } from "src/app/pipes/car-brand.pipe";
import { BrandEnum } from "src/app/classes/enums";
import moment from "moment";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { NgClass, AsyncPipe, DatePipe } from "@angular/common";
import { LoadingComponent } from "../../layout/loading/loading.component";
import { RequestFailedComponent } from "../../layout/request-failed/request-failed.component";
import { RouterLink } from "@angular/router";
import { ButtonComponent } from "../../inputs/button/button.component";
import { DatePickerComponent } from "../../inputs/date-picker/date-picker.component";
import { MatRadioGroup } from "@angular/material/radio";
import { PackageContainerComponent } from "../../layout/package-container/package-container.component";
import { IconComponent } from "../../layout/icon/icon.component";
import { TranslateModule } from "@ngx-translate/core";
import { TimeslotDisplayPipe } from "../../../pipes/timeslot-display.pipe";
import { Store } from "@ngrx/store";
import {
  AppError,
  loadAvailableTimes,
  selectAvailableTimes,
  selectBasePackages,
  selectBookingDate,
  selectBookingTime,
  selectDropoffOption,
  selectDropoffOptions,
  selectError,
  selecTimeSelectionForm,
  selectIsLoading,
  selectSelectedDate,
  selectSelectedDropoffOptionId,
  selectSelectedDropoffPackage,
  selectSelectedTime,
  updateSelectedPackages
} from "src/app/state";
import { toSignal } from "@angular/core/rxjs-interop";
import { debounceTime, switchMap } from "rxjs";
import { EbuSelectionComponent } from "../../layout/ebu-selection/ebu-selection.component";

@Component({
  selector: "app-time-selection",
  templateUrl: "./time-selection.component.html",
  styleUrl: "./time-selection.component.scss",
  imports: [
    PageContainerComponent,
    LoadingComponent,
    RequestFailedComponent,
    RouterLink,
    ButtonComponent,
    DatePickerComponent,
    ReactiveFormsModule,
    MatRadioGroup,
    PackageContainerComponent,
    NgClass,
    AsyncPipe,
    DatePipe,
    TranslateModule,
    TimeslotDisplayPipe,
    EbuSelectionComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeSelectionComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy
{
  public PackageTagEnum = PackageTagEnum;
  public PropertyEnum = PropertyEnum;
  businessProposal$ = this.businessProposalService.businessProposal$;
  businessProposalServiceWindow: Date[] = [];

  readonly availableTimes: Signal<CalendarDayTimeSlotResponse[] | null>;
  readonly selectedDropoffOptionSignal: Signal<PackageResponse | null>;
  readonly selectedDropoffId: Signal<string | null>;
  readonly dropoffOptions: Signal<PackageResponse[] | null>;
  readonly selectedDataSignal: Signal<string | null>;
  readonly isLoading: Signal<boolean>;
  readonly error: Signal<AppError | null>;
  readonly businessProposalServiceWindowSignal: Signal<Date[]>;

  constructor(
    public formService: FormService,
    public priceService: PriceService,
    private businessProposalService: BusinessProposalService,
    private carBrandPipe: CarBrandPipe,
    private store: Store
  ) {
    super();
    this.formService.timeSelectionForm.markAsUntouched();
    this.formService.timeSelectionForm.markAsPristine();
    this.formService.previousFormValue =
      this.formService.timeSelectionForm.value;

    this.availableTimes = this.store.selectSignal(selectAvailableTimes);
    this.selectedDropoffOptionSignal = this.store.selectSignal(
      selectSelectedDropoffPackage
    );
    this.dropoffOptions = this.store.selectSignal(selectDropoffOptions);
    this.selectedDropoffId = this.store.selectSignal(
      selectSelectedDropoffOptionId
    );
    this.selectedDataSignal = this.store.selectSignal(selectSelectedDate);

    this.isLoading = this.store.selectSignal(selectIsLoading);
    this.error = this.store.selectSignal(selectError);

    this.businessProposalServiceWindowSignal = computed(() => {
      const proposal = this.businessProposal$.value;
      if (!proposal?.leadCreatedAt) return [];

      const { from, to } = this._calculateServiceWindow(proposal);
      const daysDiff = to.diff(from, "days");

      return Array.from({ length: daysDiff }).map((_, i) =>
        moment(from).add(i, "days").toDate()
      );
    });

    effect(() => {
      const basePackages = this.store.selectSignal(selectBasePackages)();
      const savedForm = this.store.selectSignal(selecTimeSelectionForm)();

      if (basePackages && !savedForm) {
        this.formService.initSpecificFormFromPackages(
          this.formService.timeSelectionForm,
          basePackages.filter((p) => p.type === PackageTypeEnum.DropoffOption)
        );
      }
    });

    effect(() => {
      const selectedDate = this.selectedDataSignal();
      const selectedTime = this.store.selectSignal(selectSelectedTime)();
      const selectedDropoffId = this.selectedDropoffId();

      this.formService.timeSelectionForm.patchValue(
        {
          date: selectedDate ? new Date(selectedDate) : null,
          time: selectedTime,
          selectedContainer: selectedDropoffId
        },
        { emitEvent: false }
      );
    });

    effect(() => {
      const selectedOption = this.selectedDropoffOptionSignal();
      if (!this.time) return;

      if (selectedOption?.properties.includes(PropertyEnum.TimeSlot)) {
        this.time.setValidators(Validators.required);
      } else {
        this.time.clearValidators();
      }
      this.time.updateValueAndValidity();
      this.formService.timeSelectionForm.updateValueAndValidity();
    });

    effect(() => {
      const dates = this.availableTimes();
      const hasDateSelection = untracked(this.selectedDataSignal);
      const hasDropoffSelection = untracked(this.selectedDropoffId);
      const proposal = this.businessProposal$.value;
      if (
        this.businessProposalService.isBusinessProposalActive &&
        proposal?.leadCreatedAt
      ) {
        if (!hasDateSelection) {
          const { from: serviceWindowFrom } =
            this._calculateServiceWindow(proposal);
          const inTwoDays = moment(new Date()).add(2, "days");
          const preSelectedDate = serviceWindowFrom.isAfter(inTwoDays)
            ? serviceWindowFrom
            : inTwoDays;
          this.store.dispatch(
            selectBookingDate({
              date: preSelectedDate.toDate().toISOString()
            })
          );
        }
        if (!hasDropoffSelection) {
          const dropOffPackageOnProposal = proposal.packages.find(
            (p) => p.type === PackageTypeEnum.DropoffOption
          );
          if (dropOffPackageOnProposal) {
            this.store.dispatch(
              selectDropoffOption({
                dropOffId: dropOffPackageOnProposal.id
              })
            );
          }
        }
      } else if (dates && dates.length > 0 && !hasDateSelection) {
        this.store.dispatch(
          selectBookingDate({ date: new Date(dates[0].date).toISOString() })
        );
      }
    });

    const dateChanges = toSignal(
      this.formService.timeSelectionForm.controls.date.valueChanges
    );
    const timeChanges = toSignal(
      this.formService.timeSelectionForm.controls.time.valueChanges
    );
    const dropoffChanges = toSignal(
      this.formService.timeSelectionForm.controls.selectedContainer.valueChanges
    );

    effect(() => {
      const date = dateChanges();
      const time = timeChanges();
      if (date)
        this.store.dispatch(selectBookingDate({ date: moment(date).format() }));
    });

    effect(() => {
      const time = timeChanges();
      if (time !== undefined) this.store.dispatch(selectBookingTime({ time }));
    });

    effect(
      () => {
        const dropOffId = dropoffChanges();
        if (dropOffId) this.store.dispatch(selectDropoffOption({ dropOffId }));
      },
      { allowSignalWrites: true }
    );

    const timeFormChanges = toSignal(
      this.formService.timeSelectionForm$.pipe(
        switchMap((form) => form.valueChanges.pipe(debounceTime(100)))
      )
    );

    effect(() => {
      const changes = timeFormChanges();
      if (changes) {
        const rawValue = this.formService.timeSelectionForm.getRawValue();
        this.store.dispatch(
          updateSelectedPackages({
            selectedPackages: { timeSelectionForm: rawValue }
          })
        );
      }
    });
  }

  ngOnInit(): void {
    if (!this.availableTimes()) {
      this.store.dispatch(loadAvailableTimes());
    }
  }

  private _calculateServiceWindow(proposal: BusinessProposalResponse): {
    from: moment.Moment;
    to: moment.Moment;
  } {
    const createdAt = new Date(proposal.leadCreatedAt);
    const brandName = this.carBrandPipe.transform(proposal.vehicle);
    const toDays = brandName === BrandEnum.audi ? 45 : 32;
    const fromDays = toDays - (brandName === BrandEnum.audi ? 15 : 17);

    return {
      from: moment(createdAt).add(fromDays, "days"),
      to: moment(createdAt).add(toDays, "days")
    };
  }

  onSelectDay(date: Date): void {
    let newOption: PackageResponse | null = null;
    if (!!this.selectedDate && !!this.selectedDropoffOptionSignal())
      newOption = this.selectedDate.availableDropoffoptionIds?.includes(
        this.selectedDropoffOptionSignal().id
      )
        ? this.selectedDropoffOptionSignal()
        : null;
    this.formService.timeSelectionForm.patchValue({
      date,
      dropoffOption: newOption,
      time: null,
      calendarPackage: null
    });

    this.formService.selectedWorkshopDate = this.selectedDate;
    this.date.markAsDirty();
  }

  onTimeSelected(selectedTime: string): void {
    const hours = parseInt(selectedTime.split(":")[0], 10);
    const minutes = parseInt(selectedTime.split(":")[1], 10);
    const newDate = this.date.value;
    newDate.setHours(hours, minutes);
    this.formService.timeSelectionForm.patchValue({
      date: newDate,
      time: selectedTime === this.time.value ? null : selectedTime
    });

    this.formService.selectedWorkshopTime =
      this.formService.selectedWorkshopDate?.timeSlots?.find(
        (t: CalendarTimeSlotResponse) => t.slot === selectedTime
      );
  }

  timeSelectionParentForm(id: string): FormGroup<any> {
    return this.formService.timeSelectionForm.get(id) as FormGroup<any>;
  }

  get workshopDates(): CalendarDayTimeSlotResponse[] {
    return this.availableTimes() ?? [];
  }
  get date(): AbstractControl<Date> {
    return this.formService.timeSelectionForm.get("date");
  }
  get time(): AbstractControl {
    return this.formService.timeSelectionForm.get("time");
  }
  get dropoffOption(): AbstractControl {
    return this.formService.timeSelectionForm.get("selectedContainer");
  }

  get selectedDate(): CalendarDayTimeSlotResponse {
    if (!this.date.value) return undefined;
    return this.workshopDates.find(
      (d: CalendarDayTimeSlotResponse) =>
        d.date.toDateString() === this.date.value.toDateString()
    );
  }

  get selectedDayAvailableHours(): CalendarTimeSlotResponse[] {
    if (!this.selectedDate) return [];
    return this.selectedDate.timeSlots;
  }

  ngOnDestroy(): void {
    this.cleanUp();
  }
}
